import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withStyles from '@material-ui/core/styles/withStyles';
import exif from 'exif-js';

import { auto, block, center, cover, flex, hidden, noRepeat, pointer, relative } from 'constants/Jss';

const styles = (theme) => ({
  FilePreview: {
    display: flex,
    justifyContent: center,
    alignItems: center,
    width: '100%',
    height: '100%',
    margin: auto,
    overflow: hidden,
    position: relative,
    [theme.breakpoints.down('sm')]: {
      '& .preview-bg': {
        marginLeft: 0,
      },
    },
  },
  FileNameOnly: {
    display: flex,
    alignItems: center,
  },
  DialogContent: {
    display: flex,
    justifyContent: center,
  },
  DialogContentRoot: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  PreviewBg: {
    width: theme.mixins.filePreview.size,
    height: theme.mixins.filePreview.size,
    backgroundSize: cover,
    backgroundPosition: center,
    backgroundRepeat: noRepeat,
    borderRadius: '50%',
    [theme.breakpoints.down('sm')]: {
      width: theme.mixins.filePreview.mobileSize,
      height: theme.mixins.filePreview.mobileSize,
      '&.LeftRotated': {
        transform: 'rotate(90deg)',
      },
      '&.RightRotated': {
        transform: 'rotate(270deg)',
      },
      '&.UpsideDownRotated': {
        transform: 'rotate(180deg)',
      },
    },
  },
  Preview: {
    maxWidth: '100%',
    maxHeight: '100%',
    paddingLeft: theme.spacing(4),
    cursor: pointer,
    margin: auto,
    display: `${block} !important`,
  },
  Video: {
    objectFit: 'initial',
    width: 640,
    height: 480,
    '&.loading': {
      visibility: hidden,
    },
  },
});

export const FileTypes = {
  image: 'image',
  video: 'video',
};

class FilePreview extends Component {
  static propTypes = {
    type: PropTypes.string,
  };

  static defaultProps = {
    type: FileTypes.image,
  };

  state = {
    open: false,
    orientation: 0,
  };

  Orientations = {
    LeftRotated: 6,
    RightRotated: 8,
    UpsideDownRotated: 3,
  };

  videoPlayer = React.createRef();

  previewProps = () => {
    const { file, alt } = this.props;
    return {
      inline: {
        image: {
          alt: file.name || file.filename || alt || '',
        },
        video: {},
      },
      dialog: {
        image: {
          alt: file.name || file.filename || alt || '',
        },
        video: {
          className: 'video-preview',
          controls: true,
          onClick: this.playVideo,
        },
      },
    };
  };

  toggleDialog = () => {
    this.setState({ open: !this.state.open });
  };

  playVideo = () => {
    if (this.videoPlayer.current) {
      if (this.videoPlayer.current.paused) {
        this.videoPlayer.current.play();
      } else {
        this.videoPlayer.current.pause();
      }
    }
  };

  pauseVideo = () => {
    if (this.videoPlayer.current && !this.videoPlayer.current.paused) {
      this.videoPlayer.current.pause();
    }
  };

  getPreviewContent({ alt, className, dialog, ...props } = {}) {
    const { file, classes, type } = this.props;
    const { orientation } = this.state;

    if (type === FileTypes.image) {
      if (file && file.name) {
        const that = this;
        exif.getData(file, function () {
          const exifData = exif.pretty(this);
          if (exifData) {
            if (orientation !== exif.getTag(this, 'Orientation')) {
              that.setState({ orientation: exif.getTag(this, 'Orientation') });
            }
          }
        });
      }
      return dialog ? (
        <img src={file.preview || file.url} alt={alt} className={className} {...props} />
      ) : (
        <div
          className={classNames('preview-bg', classes.PreviewBg, {
            LeftRotated: orientation === this.Orientations.LeftRotated,
            RightRotated: orientation === this.Orientations.RightRotated,
            UpsideDownRotated: orientation === this.Orientations.UpsideDownRotated,
          })}
          style={{ backgroundImage: `url(${file.preview || file.url})` }}
          {...props}
        />
      );
    } else if (type === FileTypes.video) {
      return (
        <video
          {...props}
          className={classNames(className, classes.Video)}
          ref={this.videoPlayer}
          key={file.preview || file.url}
        >
          <source src={file.preview || file.url} />
          {file.name || file.filename}
        </video>
      );
    } else {
      return <div className={classes.FileNameOnly}>{file.name || file.filename}</div>;
    }
  }

  render() {
    const { file, type, classes } = this.props;

    return (
      <div className={classes.FilePreview}>
        {file &&
          (file.preview || file.url) &&
          this.getPreviewContent({
            className: classes.Preview,
            ...this.previewProps().inline[type],
          })}
      </div>
    );
  }
}

export default withStyles(styles)(FilePreview);
